import React, { useState } from 'react';
import MaskedInput from 'react-text-mask';
import { validation } from './validation';
import styles from '../../assets/css/input.module.css';
import { InputElement } from '../../schema/input.schema';
import { ValidationResult } from '../../validators';
import { IconButton, InputAdornment, styled, TextField, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { FormDataValue } from '../../hooks/useFormData';
import AlertIcon from './AlertIcon';

interface InputComponentProps {
	element: InputElement;
	onChange: (key: string, value: FormDataValue) => void;
	onValidationChange: (key: string, value: boolean) => void;
	info?: string;
}

const InputComponent: React.FC<InputComponentProps> = ({ element, onChange, onValidationChange, info }) => {
	const { label, read_only, placeholder, sensitive, validator, mask, prefill_value, required, key } = element;
	const [value, setValue] = useState(prefill_value || '');
	const [error, setError] = useState<string | undefined>();

	const [touched, setTouched] = useState<boolean>(false);

	const inputType = sensitive ? 'password' : 'text';

	const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const newValue = event.target.value;
		setValue(newValue);
		onChange(key, newValue);
		performValidation(newValue);
	};

	const handleBlur = () => {
		setTouched(true);
	};

	const performValidation = (valueToValidate: string) => {
		let unmaskedValue = '';

		if (mask) {
			valueToValidate.split('').forEach((char, index) => {
				const maskChar = mask[index];
				if (maskChar && isRegExp(maskChar) && maskChar.test(char)) {
					unmaskedValue += char;
				}
			});
		} else {
			unmaskedValue = valueToValidate;
		}

		const validationResult: ValidationResult = validation(unmaskedValue, validator, required, label);
		setError(validationResult.isValid ? undefined : validationResult.errorMessage);
		onValidationChange(key, validationResult.isValid);
	};

	const inputProps = {
		sx: {
			border: '1px solid #969696',
			background: '#FFFFFF',
			borderRadius: '5px',
			width: '100%',
			'& .MuiInputLabel-root': {
				color: '#707070'
			},
			'& .Mui-focused': {
				color: '#707070'
			},
			'& .MuiFilledInput-root': {
				borderBottomLeftRadius: '5px !important',
				borderBottomRightRadius: '5px !important',
				borderTopLeftRadius: '5px !important',
				borderTopRightRadius: '5px !important'
			},
			'& .MuiInputBase-root': {
				background: 'white !important',
				'& .MuiInputBase-input:focus': {
					border: 'none'
				}
			},
			'&:focus-within': {
				border: `1px solid ${error && touched ? '#FF0000' : '#007bff'} !important`
			}
		},
		inputProps: {
			underline: {
				'&&&:before': {
					borderBottom: 'none'
				},
				'&&:after': {
					borderBottom: 'none'
				}
			},
			style: {
				color: `${error && touched ? '#FF0000' : 'black'}`,
				fontWeight: '300'
			}
		},
		InputProps: {
			disableUnderline: true,
			startAdornment: error && touched && (
				<InputAdornment
					position="start"
					className="input-adornment"
					sx={{
						backgroundColor: '#fffff !important',
						borderRadius: '5px'
					}}
				>
					<AlertIcon />
				</InputAdornment>
			),
			endAdornment: info && (
				<InputAdornment
					position="end"
					className="input-adornment"
					sx={{
						backgroundColor: '#fffff !important',
						borderRadius: '5px'
					}}
				>
					<CustomWidthTooltip title={info}>
						<IconButton
							className={styles['iconButton']}
							style={{ border: '1px solid #707070' }}
							size="small"
							aria-label="help"
						>
							?
						</IconButton>
					</CustomWidthTooltip>
				</InputAdornment>
			)
		},
		InputLabelProps: {
			style: {
				color: `${error && touched ? '#FF0000' : '#707070'}`,
				fontSize: '18px',
				background: 'white',
				fontWeight: '300'
			}
		}
	};

	const renderInput = mask ? (
		<MaskedInput
			id={element.key}
			type={inputType}
			mask={mask}
			value={value}
			onChange={handleChange}
			onBlur={handleBlur}
			placeholder={placeholder || ''}
			readOnly={read_only}
			render={(ref, maskedProps) => (
				<TextField
					inputRef={ref}
					{...maskedProps}
					fullWidth
					variant="filled"
					disabled={read_only}
					label={label}
					{...inputProps}
				/>
			)}
		/>
	) : (
		<TextField
			id={element.key}
			type={inputType}
			value={value}
			variant="filled"
			label={label}
			onChange={handleChange}
			onBlur={handleBlur}
			placeholder={placeholder || ''}
			disabled={read_only}
			{...inputProps}
		/>
	);

	return (
		<div className={styles['input-container']}>
			{renderInput}
			{error && touched && <span className={styles['error-message']}>{error}</span>}
		</div>
	);
};

export default InputComponent;

const isRegExp = (value: any): value is RegExp => {
	return value instanceof RegExp;
};

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))({
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 200
	}
});
